import { Asset } from '../../../models/image/dto/asset';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { UpdateAssetRequest } from '../../../models/image/requests/update-asset-request';
import { MediaManagerFileSystem } from '../../../models/media-manager/media-manager-file-system';
import { ArrayUtils } from '../../../utils/array-utils';
import {
  getProtectedSpecialAncestorForAsset,
  getProtectedTopLevelSpecialFolderForAssetId
} from '../policy/special-folder-policy';

/**
 * Verify an asset update request against the current filesystem and asset library. Returns `null` when valid,
 * otherwise a `.`-joined error message. Errors accumulate across parent-change and metadata-change checks so a
 * single failure surfaces all reasons at once.
 */
export function preflightAssetUpdateValidation(
  req: UpdateAssetRequest | Asset,
  currentAsset: Asset,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): string | null {
  const errors: string[] = [];
  const protectedTopLevelFolder = getProtectedTopLevelSpecialFolderForAssetId(
    currentAsset.getUniqueIdentifier(),
    filesystem,
    assetLibrary
  );
  const parentChanged = req.parentFolderId !== currentAsset.parentFolderId;
  const displayNameChanged = req.displayName !== currentAsset.displayName;
  const fileNameChanged = req.fileName !== currentAsset.fileName;
  const mediaClassChanged = req.mediaClass !== currentAsset.mediaClass;
  const tagsChanged = !ArrayUtils.arraysEqualAsSets(req.tags, currentAsset.tags);
  const metadataChanged = displayNameChanged || fileNameChanged || mediaClassChanged || tagsChanged;
  if (parentChanged) {
    const parentChangeErrors = preflightAssetParentChangeErrors(req, currentAsset, filesystem, assetLibrary);
    if (parentChangeErrors?.length) errors.push(...parentChangeErrors);
  }
  if (metadataChanged) {
    const metadataChangeErrors = protectedTopLevelFolder
      ? preflightProtectedAssetMetadataChangeErrors(req, fileNameChanged, mediaClassChanged, tagsChanged)
      : preflightAssetMetadataChangeErrors(req, fileNameChanged, mediaClassChanged, tagsChanged);
    if (metadataChangeErrors?.length) errors.push(...metadataChangeErrors);
  }
  return errors.length ? errors.join('. ') : null;
}

/**
 * Parent-folder change constraints: target folder must exist, must be different from current, and the asset's
 * current subtree cannot be protected.
 */
export function preflightAssetParentChangeErrors(
  req: UpdateAssetRequest | Asset,
  currentAsset: Asset,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): string[] {
  const errors: string[] = [];
  const oldId = currentAsset.getUniqueIdentifier();
  const currentParentId = currentAsset.parentFolderId;
  const targetParentId = req.parentFolderId;
  if (!targetParentId?.trim()) {
    errors.push('Updated parent folder ID is not valid');
    return errors;
  }
  if (!filesystem.assetExists(oldId)) {
    errors.push(`Current asset '${oldId}' does not exist in the filesystem`);
  }
  if (!filesystem.folderExists(targetParentId)) {
    errors.push(`Target parent folder '${targetParentId}' does not exist in the filesystem`);
  }
  if (currentParentId === targetParentId) {
    errors.push(`Asset '${oldId}' is already in folder '${targetParentId}'`);
  }
  const protectedAncestorFolder = getProtectedSpecialAncestorForAsset(currentAsset, filesystem, assetLibrary);
  if (protectedAncestorFolder) {
    errors.push(`Cannot move asset '${oldId}' because it is inside protected folder "${protectedAncestorFolder.name}"`);
  }
  return errors;
}

/** Standard field validation for asset metadata changes (filename, mediaClass, tags). */
export function preflightAssetMetadataChangeErrors(
  req: UpdateAssetRequest | Asset,
  fileNameChanged: boolean,
  mediaClassChanged: boolean,
  tagsChanged: boolean
): string[] {
  const errors: string[] = [];
  if (fileNameChanged && !req.fileName?.trim()) {
    errors.push('File name is not valid');
  }
  if (mediaClassChanged && !req.mediaClass?.trim()) {
    errors.push('Media category is not valid');
  }
  if (tagsChanged && !Array.isArray(req.tags)) {
    errors.push('Tags must be an array');
  }
  if (tagsChanged && req.tags?.some(tag => !tag || !tag.trim())) {
    errors.push('Tags cannot contain empty values');
  }
  return errors;
}

/**
 * Same rules as {@link preflightAssetMetadataChangeErrors} today, but kept as its own function so future
 * protected-folder-specific policy (e.g. locking tags on POS media) has an obvious hook.
 */
export function preflightProtectedAssetMetadataChangeErrors(
  req: UpdateAssetRequest | Asset,
  fileNameChanged: boolean,
  mediaClassChanged: boolean,
  tagsChanged: boolean
): string[] {
  return preflightAssetMetadataChangeErrors(req, fileNameChanged, mediaClassChanged, tagsChanged);
}
